import React from 'react';
import { useIntl } from 'react-intl';
import { Button, CircularProgress } from '@mui/material';
import Modal from 'Components/Modal';
import { hasEventId, useDeviceReadingRawData } from './hooks/useDeviceReadingRawData';
import { getRawDataAdapter } from './manufacturers/registry';
import { OEM_DATA_ICON } from './constants';
import { RawDataBody } from './components/RawDataBody';
import { useRemoteCommandsAccess } from './remoteCommands/hooks/useRemoteCommandsAccess';
import { RemoteCommandsTab } from './remoteCommands/components/RemoteCommandsTab';
import { RemoteCommandHistoryTab } from './remoteCommands/components/RemoteCommandHistoryTab';
import { REMOTE_COMMAND_HISTORY_TAB, REMOTE_COMMANDS_TAB } from './remoteCommands/constants';
import './oem-data-modal.scss';

/**
 * State switch, in z1-ui-main's order on purpose: a disabled query stays
 * `isPending` forever, so the permanent gate (no event id) must be answered
 * before the spinner is considered.
 */
const renderBody = ({ eventId, query, showRawJson, extraTabs, messages }) => {
	if (!hasEventId(eventId)) {
		return <p className="oem-data-modal-message">{messages['oemDataModal_noEventId']}</p>;
	}
	if (query.isPending) {
		return (
			<div className="oem-data-modal-loading" role="status">
				<CircularProgress size={20} />
				{messages['oemDataModal_loading']}
			</div>
		);
	}
	if (query.isError) {
		return (
			<div>
				<p className="oem-data-modal-error">{messages['oemDataModal_error']}</p>
				<Button variant="outlined" size="small" onClick={() => query.refetch()}>
					{messages['oemDataModal_retry']}
				</Button>
			</div>
		);
	}
	if (query.data === null || query.data.rawMessage === null) {
		return <EmptyMessage eventId={eventId} />;
	}
	return <RawDataBody key={query.data.id} record={query.data} showRawJson={showRawJson} extraTabs={extraTabs} />;
};

/** Feature tabs appended after the manufacturer tabs; none unless the unit is allowed. */
const buildExtraTabs = ({ remoteCommandsAllowed, unitLabel }) => {
	if (!remoteCommandsAllowed) return [];
	return [
		{
			value: REMOTE_COMMANDS_TAB,
			labelKey: 'oemDataModal_remote_tab',
			Content: RemoteCommandsTab,
			contentProps: { unitLabel },
		},
		{ value: REMOTE_COMMAND_HISTORY_TAB, labelKey: 'oemDataModal_remote_historyTab', Content: RemoteCommandHistoryTab },
	];
};

const EmptyMessage = ({ eventId }) => {
	const { formatMessage } = useIntl();
	return (
		<p className="oem-data-modal-message">{formatMessage({ id: 'oemDataModal_empty' }, { eventId: String(eventId) })}</p>
	);
};

/** "Unit 12 · CerebrumX" once the reading (and so its manufacturer) is known. */
const buildTitle = (unitLabel, record, messages) => {
	const label = unitLabel || messages['oemDataModal_title'];
	if (!record) return label;
	const adapter = getRawDataAdapter(record.manufacturerName);
	const subtitle = adapter ? messages[adapter.nameKey] : record.manufacturerName;
	if (!subtitle) return label;
	return `${label} · ${subtitle}`;
};

/**
 * Raw OEM data viewer for one reading `(unitId, eventId)` — the latest one or
 * a historical trail point. The caller passes a snapshot taken on click, so the
 * live feed cannot swap the event under the reader. Fetches only while
 * mounted (i.e. open); the payload is rendered per manufacturer (CerebrumX:
 * Live / Energy / Diagnostics / Location / Vehicle) with the JSON view as the
 * fallback for unknown manufacturers and, when `showRawJson`, as an extra tab.
 * Allowed CerebrumX units also get the Remote commands and Command history tabs (P3-53961).
 */
export const OemDataModal = ({ unitId, eventId, unitLabel, onClose, showRawJson = true }) => {
	const { messages } = useIntl();
	const query = useDeviceReadingRawData(unitId, eventId);
	const { allowed } = useRemoteCommandsAccess({ manufacturerName: query.data?.manufacturerName });
	const extraTabs = buildExtraTabs({ remoteCommandsAllowed: allowed, unitLabel });

	return (
		<Modal
			open
			size="medium"
			headerIcon={OEM_DATA_ICON}
			title={buildTitle(unitLabel, query.data, messages)}
			handleClose={onClose}
			onClose={onClose}
			className="oem-data-modal"
		>
			<div className="oem-data-modal-body">{renderBody({ eventId, query, showRawJson, extraTabs, messages })}</div>
		</Modal>
	);
};
